export interface Size {
  width: number;
  height: number;
}

/** Where the content sits in its frame: scaled by `scale` from its top-left corner, then moved by `x`/`y` px. */
export interface Viewport {
  scale: number;
  x: number;
  y: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Two fingers on the frame. */
export interface Pinch {
  distance: number;
  /** Halfway between the fingers, in frame px. */
  middle: Point;
}

/** Zoom range for the buyer's own zooms. */
const MIN_SCALE = 0.1;
const MAX_SCALE = 4;

/** Largest scale `fit` uses, so a small Section doesn't fill the frame with huge seats. */
const FIT_MAX_SCALE = 1.6;

/** Shows the whole content, centred in the frame. */
export function fit(frame: Size, content: Size): Viewport {
  const scale = Math.min(frame.width / content.width, frame.height / content.height, FIT_MAX_SCALE);

  return {
    scale,
    x: (frame.width - content.width * scale) / 2,
    y: (frame.height - content.height * scale) / 2,
  };
}

/** Multiplies the scale by `factor`, keeping `point` (in frame px) over the same spot of the content. */
export function zoomAt(view: Viewport, factor: number, point: Point): Viewport {
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, view.scale * factor));
  const applied = scale / view.scale;

  return {
    scale,
    x: point.x - (point.x - view.x) * applied,
    y: point.y - (point.y - view.y) * applied,
  };
}

/** Below this on-screen seat size (px) a finger can't hit one seat reliably. */
const MIN_TAP_SIZE = 22;

/** On-screen seat size (px) a tap zoom brings seats to. */
const TAP_ZOOM_SIZE = 32;

/** Whether a tap zooms in instead of toggling a seat, for seats drawn `seatSize` px wide. */
export function shouldTapZoom(view: Viewport, seatSize: number): boolean {
  return view.scale * seatSize < MIN_TAP_SIZE;
}

/** Zooms in around the tapped point until seats are big enough to tap. */
export function tapZoom(view: Viewport, point: Point, seatSize: number): Viewport {
  return zoomAt(view, TAP_ZOOM_SIZE / seatSize / view.scale, point);
}

export function pinchOf(a: Point, b: Point): Pinch {
  return { distance: Math.hypot(a.x - b.x, a.y - b.y), middle: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
}

/** Follows two fingers moving from `from` to `to`: zooms by their spread and pans by their slide. */
export function pinchZoom(view: Viewport, from: Pinch, to: Pinch): Viewport {
  const zoomed = zoomAt(view, to.distance / from.distance, from.middle);

  return { ...zoomed, x: zoomed.x + to.middle.x - from.middle.x, y: zoomed.y + to.middle.y - from.middle.y };
}
